import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ProgressiveList from "./ProgressiveList";

/**
 * Client-side pagination wrapper around `ProgressiveList`.
 *
 * If `items.length <= pageSize` the list renders identically to a bare
 * `ProgressiveList` (no controls, no page state) so short lists cost nothing.
 * When the list is larger, controls appear both above and below the list and
 * only the current page's slice is rendered.
 *
 * Page transitions never reload the page — only local state changes — and
 * the top of the list scrolls into view so the first row of the new page is
 * always visible. If `items` shrinks, `page` is clamped so we never render
 * past the end.
 *
 * Props:
 *   items       — full list to paginate
 *   renderItem  — per-item render function (passed straight to ProgressiveList)
 *   pageSize    — default 100
 *   label       — short noun for tooltips ("achievements", "items") — display only
 */
export default function PaginatedList({
  items,
  renderItem,
  pageSize = 100,
  label = "items",
  // Only show pagination controls once the list crosses this size. Below
  // the threshold the component renders exactly like ProgressiveList.
  // Defaults to `pageSize` so a 100-item list is never paginated but a
  // 101-item list is; pass a larger number (e.g. 500) to defer pagination.
  activateAt = null,
}) {
  const [page, setPage] = useState(1);
  const topRef = useRef(null);

  const threshold = activateAt ?? pageSize;
  const paginate = items.length > threshold;
  const totalPages = paginate ? Math.max(1, Math.ceil(items.length / pageSize)) : 1;

  // Clamp page when the underlying list shrinks below the current page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  // Compute displayed slice for the current page. Memoised so React can
  // reconcile children efficiently between page changes.
  const pageItems = useMemo(() => {
    if (!paginate) return items;
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, page, pageSize, paginate]);

  if (!paginate) {
    // Short-circuit: nothing to paginate.
    return <ProgressiveList items={items} renderItem={renderItem} />;
  }

  const goToPage = (n) => {
    const clamped = Math.min(Math.max(1, n), totalPages);
    if (clamped === page) return;
    setPage(clamped);
    // Scroll the top of the list into view so the first row of the new
    // page is always visible without pushing the header off-screen.
    // requestAnimationFrame lets React commit the new slice first.
    requestAnimationFrame(() => {
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const startIdx = (page - 1) * pageSize + 1;
  const endIdx = Math.min(items.length, page * pageSize);

  return (
    <>
      <div ref={topRef} className="scroll-mt-4" />
      <PaginationBar
        page={page}
        totalPages={totalPages}
        startIdx={startIdx}
        endIdx={endIdx}
        total={items.length}
        label={label}
        onPageChange={goToPage}
      />
      <ProgressiveList items={pageItems} renderItem={renderItem} />
      <PaginationBar
        page={page}
        totalPages={totalPages}
        startIdx={startIdx}
        endIdx={endIdx}
        total={items.length}
        label={label}
        onPageChange={goToPage}
      />
    </>
  );
}

function PaginationBar({ page, totalPages, startIdx, endIdx, total, label, onPageChange }) {
  const pages = buildPageList(page, totalPages);
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 py-2 my-2 border-y border-white/5 text-xs text-white/60"
      data-testid="pagination-bar"
    >
      <span className="tabular-nums">
        {startIdx}–{endIdx} of {total} {label}
      </span>
      <div className="flex items-center gap-1">
        <PageBtn onClick={() => onPageChange(page - 1)} disabled={page === 1} title="Previous page">
          <ChevronLeft className="w-3.5 h-3.5" />
        </PageBtn>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`e${i}`} className="px-1 text-white/30 select-none">…</span>
          ) : (
            <PageBtn
              key={p}
              onClick={() => onPageChange(p)}
              active={p === page}
              title={`Go to page ${p}`}
            >
              {p}
            </PageBtn>
          )
        )}
        <PageBtn onClick={() => onPageChange(page + 1)} disabled={page === totalPages} title="Next page">
          <ChevronRight className="w-3.5 h-3.5" />
        </PageBtn>
      </div>
    </div>
  );
}

function PageBtn({ onClick, disabled, active, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`min-w-[1.75rem] h-7 px-2 rounded text-xs tabular-nums flex items-center justify-center border transition-colors ${
        active
          ? "bg-cyan-500/25 border-cyan-400/60 text-cyan-200 font-semibold"
          : "border-white/10 hover:border-white/30 hover:bg-white/5 text-white/70 disabled:opacity-30 disabled:hover:border-white/10 disabled:hover:bg-transparent"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Build a compact page-number list with ellipses around the active page.
 * Examples:
 *   1..10, active=1  -> [1, 2, 3, 4, "…", 10]
 *   1..10, active=5  -> [1, "…", 4, 5, 6, "…", 10]
 *   1..10, active=10 -> [1, "…", 7, 8, 9, 10]
 */
function buildPageList(page, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const set = new Set([1, totalPages, page, page - 1, page + 1]);
  const sorted = [...set].filter(n => n >= 1 && n <= totalPages).sort((a, b) => a - b);
  const withGaps = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      withGaps.push("…");
    }
    withGaps.push(sorted[i]);
  }
  return withGaps;
}
